import { Component, input, output, signal, AfterViewInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-carousel',
  imports: [CommonModule],
  template: `
    <div class="relative" #carouselContainer>
      <div 
        #carouselTrack
        class="overflow-hidden rounded-lg"
        (touchstart)="onTouchStart($event)"
        (touchmove)="onTouchMove($event)"
        (touchend)="onTouchEnd()">
        <div 
          [class]="trackClasses"
          [style.transform]="'translateX(-' + (currentIndex() * 100) + '%)'">
          @for (item of items(); track $index) {
            <div [class]="slideClasses">
              <ng-container *ngTemplateOutlet="itemTemplate; context: { $implicit: item, index: $index }"></ng-container>
            </div>
          }
        </div>
      </div>
      
      @if (showIndicators()) {
        <div class="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          @for (item of items(); track $index) {
            <button
              type="button"
              [class]="getIndicatorClasses($index)"
              (click)="goToSlide($index)"
              [attr.aria-label]="'Go to slide ' + ($index + 1)"></button>
          }
        </div>
      }
      
      @if (showArrows()) {
        <button
          type="button"
          [class]="arrowClasses + ' left-4'"
          (click)="previous()"
          [attr.aria-label]="'Previous slide'">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          type="button"
          [class]="arrowClasses + ' right-4'"
          (click)="next()"
          [attr.aria-label]="'Next slide'">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      }
    </div>
    
    <ng-template #itemTemplate let-item let-index="index">
      <ng-content [ngTemplateOutlet]="itemTemplate || defaultTemplate"></ng-content>
    </ng-template>
  `,
  styles: []
})
export class CarouselComponent implements AfterViewInit, OnDestroy {
  items = input<any[]>([]);
  autoplay = input<boolean>(false);
  interval = input<number>(3000);
  showIndicators = input<boolean>(true);
  showArrows = input<boolean>(true);
  loop = input<boolean>(true);

  slideChangeEvent = output<number>();

  @ViewChild('carouselTrack', { static: false }) carouselTrack!: ElementRef<HTMLElement>;

  currentIndex = signal(0);
  touchStartX = 0;
  touchEndX = 0;
  private autoplayInterval?: number;

  ngAfterViewInit(): void {
    if (this.autoplay()) {
      this.startAutoplay();
    }
  }

  ngOnDestroy(): void {
    this.stopAutoplay();
  }

  startAutoplay(): void {
    this.autoplayInterval = window.setInterval(() => {
      this.next();
    }, this.interval());
  }

  stopAutoplay(): void {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
    }
  }

  next(): void {
    const nextIndex = this.currentIndex() + 1;
    if (nextIndex >= this.items().length) {
      if (this.loop()) {
        this.currentIndex.set(0);
      }
    } else {
      this.currentIndex.set(nextIndex);
    }
    this.slideChangeEvent.emit(this.currentIndex());
  }

  previous(): void {
    const prevIndex = this.currentIndex() - 1;
    if (prevIndex < 0) {
      if (this.loop()) {
        this.currentIndex.set(this.items().length - 1);
      }
    } else {
      this.currentIndex.set(prevIndex);
    }
    this.slideChangeEvent.emit(this.currentIndex());
  }

  goToSlide(index: number): void {
    if (index >= 0 && index < this.items().length) {
      this.currentIndex.set(index);
      this.slideChangeEvent.emit(index);
    }
  }

  onTouchStart(event: TouchEvent): void {
    this.touchStartX = event.touches[0].clientX;
    this.stopAutoplay();
  }

  onTouchMove(event: TouchEvent): void {
    this.touchEndX = event.touches[0].clientX;
  }

  onTouchEnd(): void {
    if (this.touchStartX - this.touchEndX > 50) {
      this.next();
    } else if (this.touchEndX - this.touchStartX > 50) {
      this.previous();
    }
    if (this.autoplay()) {
      this.startAutoplay();
    }
  }

  get trackClasses(): string {
    return 'flex transition-transform duration-500 ease-in-out';
  }

  get slideClasses(): string {
    return 'min-w-full flex-shrink-0';
  }

  get arrowClasses(): string {
    return 'absolute top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800 shadow-lg transition-colors z-10';
  }

  getIndicatorClasses(index: number): string {
    const classes = ['w-2', 'h-2', 'rounded-full', 'transition-all', 'duration-200'];
    if (index === this.currentIndex()) {
      classes.push('bg-blue-600', 'dark:bg-blue-500', 'w-8');
    } else {
      classes.push('bg-gray-300', 'dark:bg-gray-600', 'hover:bg-gray-400', 'dark:hover:bg-gray-500');
    }
    return classes.join(' ');
  }
}

